import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import BackButton from '../../components/BackButton';
import IconButton from '../../components/IconButton/IconButton';
import Navigation from '../../components/Navigation/Navigation';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { sendFAQstring } from '../../services/Analytics';
import styles from './FAQ.module.scss';

const sendFAQString = debounce((search: string) => {
  sendFAQstring(search);
}, 2000);

interface FAQHeaderProps {
  onSearch: (search: string) => void;
  searchResultCount?: number;
  searchText: string;
}

export const FAQHeader: FC<FAQHeaderProps> = ({ onSearch, searchText, searchResultCount }) => {
  const { t } = useTranslation();
  const { stylesheet } = useContext(StylesheetContext);

  const search = useCallback(
    (event: { target: { value: string } }): void => {
      onSearch(event.target.value);
      sendFAQString(event.target.value);
    }, [onSearch]);

  const clearSearch = useCallback(() => {
    onSearch('');
  }, [onSearch]);

  return (
    <section className={styles.faqHeader}>
      <Navigation className={styles.navigationContainer}>
        <BackButton text={t('button:back')} useRouter/>
      </Navigation>
      <div className={styles.faqHeaderTitle}>
        <h1>{t('faq:title1')} <span className={styles.headerHighlight}>{t('faq:title2')}</span></h1>
        <p className={styles.faqDescription}>{t('faq:text', { partner: stylesheet.settings.partnerName })}</p>
      </div>
      <div className={classNames(styles.searchBar, {
        'partner-initial-border-color-rounded': !!searchText,
      })}>
        <input
          className={styles.input}
          id="search"
          onChange={search}
          placeholder={t('faq:search')}
          type="search"
          value={searchText}
        />
        <IconButton
          className={styles.searchIcon}
          icon={searchText ? 'close' : 'search'}
          onClick={searchText ? clearSearch : undefined}
        />
      </div>
      {typeof searchResultCount === 'number' && (
        <p className={styles.searchResultCount}>
          {t('faq:searchResultCount', { count: searchResultCount })}
          {' '}<span className={styles.searchResultCountFound}>{t('faq:searchResultCountFound')}</span>
        </p>
      )}
    </section>
  );
};
